import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createResponsibilityMap,
  deleteResponsibilityMap,
  getResponsibilityMapOptions,
  listResponsibilityMap,
  updateResponsibilityMap,
  type CreateResponsibilityMapBody,
  type ResponsibilityMap,
  type ResponsibilityMapList,
  type ResponsibilityMapOptions,
  type UpdateResponsibilityMapBody,
} from "../api/responsibilityMap";
import { listAllUsers, type AdminUser } from "../api/users";

const KEY = "responsibility-map";

export type ResponsibilityRecipientUsers = {
  internal: AdminUser[];
};

/** All active mappings (§L — HTTP via TanStack Query). */
export function useResponsibilityMap() {
  return useQuery<ResponsibilityMapList>({
    queryKey: [KEY, "list"],
    queryFn: () => listResponsibilityMap(),
    staleTime: 30_000,
  });
}

/** Dropdown options — loaded when the modal opens. */
export function useResponsibilityMapOptions(enabled: boolean) {
  return useQuery<ResponsibilityMapOptions>({
    queryKey: [KEY, "options"],
    queryFn: () => getResponsibilityMapOptions(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Registered recipient suggestions. The mapping remains email-first (MD-06);
 * every active internal user is offered in both responsibility slots. The
 * directory API remains authoritative for active-state and caller scope.
 */
export function useResponsibilityRecipientUsers(enabled: boolean) {
  return useQuery<ResponsibilityRecipientUsers>({
    queryKey: ["users", "list", "responsibility-map-internal-candidates"],
    queryFn: async () => {
      const internal = await listAllUsers({ isInternal: true });
      return { internal };
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateResponsibilityMap() {
  const queryClient = useQueryClient();
  return useMutation<ResponsibilityMap, Error, CreateResponsibilityMapBody>({
    mutationFn: (body) => createResponsibilityMap(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useUpdateResponsibilityMap() {
  const queryClient = useQueryClient();
  return useMutation<ResponsibilityMap, Error, { id: string; body: UpdateResponsibilityMapBody }>({
    mutationFn: ({ id, body }) => updateResponsibilityMap(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

/** Soft-delete via DELETE /v1/responsibility-map/:id. */
export function useDeleteResponsibilityMap() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteResponsibilityMap(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
